import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useRouteContext } from '@tanstack/react-router';
import { Button, Flex, Form, Input, Modal, Typography, message } from 'antd';
import { Plus, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react';

import {
  useArchiveUser,
  useCreateUser,
  useImpersonateUser,
  useRestoreUser,
  useSetUserSessionRecording,
} from '../../../api';
import {
  SuperadminUsersTable,
  UsersStatusFilterDropdown,
  useSuperadminUsersInfiniteQuery,
} from '../../../modules/superadmin';
import {
  type SuperadminUsersRecordStatusFilter,
  UsersSearchParamsSchema,
} from '../../../modules/superadmin/utils/superadminUsersSearch';
import { TableSurface } from '../../../ui/components/table-surface';
import { useDebouncedValue } from '../../../ui/hooks';
import { useLayoutInsets } from '../../../ui/providers/layout-insets';

const UsersRoute = () => {
  const navigate = Route.useNavigate();
  const { account } = useRouteContext({ from: '/_authenticated' });
  const insets = useLayoutInsets();
  const { search, recordStatus, sortBy, sortDirection } = Route.useSearch({
    structuralSharing: false,
  });
  const effectiveRecordStatus = recordStatus ?? 'active';
  const [prevSearch, setPrevSearch] = useState(search);
  const [searchInput, setSearchInput] = useState(search ?? '');
  if (search !== prevSearch) {
    setPrevSearch(search);
    setSearchInput(search ?? '');
  }
  const [debouncedSearch] = useDebouncedValue(searchInput, 300);
  const [selectedRowKeys, setSelectedRowKeys] = useState<Set<string>>(new Set());
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 768px)').matches);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createForm] = Form.useForm<{
    firstName: string;
    lastName: string;
    email: string;
    password: string;
  }>();
  const [impersonateTarget, setImpersonateTarget] = useState<{
    userId: string;
    email: string;
  } | null>(null);
  const [impersonateForm] = Form.useForm<{ reason: string }>();

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)');
    const handleChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    const trimmed = debouncedSearch.trim() || undefined;
    const currentSearch = (search ?? '').trim() || undefined;
    if (trimmed !== currentSearch) {
      navigate({
        to: '/superadmin/users',
        search: (prev) => ({
          ...prev,
          search: trimmed,
        }),
        replace: true,
        resetScroll: false,
      });
    }
  }, [debouncedSearch, search, navigate]);

  const listParams = useMemo(
    () => ({
      search: search || undefined,
      recordStatus: effectiveRecordStatus,
      sortBy: sortBy ?? 'created',
      sortDirection: sortDirection ?? 'desc',
    }),
    [search, effectiveRecordStatus, sortBy, sortDirection],
  );

  useEffect(() => {
    queueMicrotask(() => {
      setSelectedRowKeys(new Set());
    });
  }, [search, recordStatus, sortBy, sortDirection]);

  const queryClient = useQueryClient();
  const { data, isPending, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useSuperadminUsersInfiniteQuery(listParams);
  const archiveMutation = useArchiveUser({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['superadmin', 'users'] });
      },
    },
  });
  const restoreMutation = useRestoreUser({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['superadmin', 'users'] });
      },
    },
  });
  const createSuperadminMutation = useCreateUser({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['superadmin', 'users'] });
      },
    },
  });

  const sessionRecordingMutation = useSetUserSessionRecording({
    mutation: {
      onSuccess: (_data, { data }) => {
        queryClient.invalidateQueries({ queryKey: ['superadmin', 'users'] });
        message.success(data.enabled ? 'Session recording enabled' : 'Session recording disabled');
      },
      onError: () => {
        message.error('Failed to change session recording');
      },
    },
  });

  const impersonateMutation = useImpersonateUser();

  const handleOpenCreateModal = useCallback(() => {
    createForm.resetFields();
    setIsCreateModalOpen(true);
  }, [createForm]);

  const handleCloseCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
    createForm.resetFields();
  }, [createForm]);

  const handleCreateSuperadmin = useCallback(async () => {
    try {
      const values = await createForm.validateFields();
      createSuperadminMutation.mutate(
        {
          data: {
            firstName: values.firstName.trim(),
            lastName: values.lastName.trim(),
            email: values.email.trim(),
            password: values.password,
            role: 'superadmin',
          },
        },
        {
          onSuccess: () => {
            message.success('SuperAdmin created');
            handleCloseCreateModal();
          },
          onError: (error: unknown) => {
            const msg = (error as { response?: { data?: { message?: string } } })?.response?.data
              ?.message;
            message.error(msg ?? 'Failed to create SuperAdmin');
          },
        },
      );
    } catch {
      // Form validation errors are shown by Form.Item rules.
    }
  }, [createForm, createSuperadminMutation, handleCloseCreateModal]);

  const handleImpersonate = (userId: string, email: string) => {
    impersonateForm.resetFields();
    setImpersonateTarget({ userId, email });
  };

  const handleImpersonateConfirm = async () => {
    if (!impersonateTarget) return;
    try {
      const values = await impersonateForm.validateFields();
      impersonateMutation.mutate(
        { userId: impersonateTarget.userId, data: { reason: values.reason.trim() } },
        {
          onSuccess: (response) => {
            const { customToken } = response.data;
            const url = new URL('/impersonate', window.location.origin);
            url.hash = `token=${encodeURIComponent(customToken)}`;
            window.open(url.toString(), '_blank', 'noopener');
            setImpersonateTarget(null);
            impersonateForm.resetFields();
          },
          onError: (error: unknown) => {
            const msg = (error as { response?: { data?: { message?: string } } })?.response?.data
              ?.message;
            message.error(msg ?? 'Failed to start impersonation session');
          },
        },
      );
    } catch {
      // Form validation errors are shown by Form.Item rules.
    }
  };

  const handleArchive = useCallback(
    (userId: string) => {
      Modal.confirm({
        title: 'Archive user?',
        content: 'The user will be archived and will not be able to sign in until restored.',
        okText: 'Archive',
        okButtonProps: { danger: true, 'data-testid': 'confirm' },
        cancelText: 'Cancel',
        onOk: () => archiveMutation.mutate({ userId }),
      });
    },
    [archiveMutation],
  );

  const handleRestore = useCallback(
    (userId: string) => {
      restoreMutation.mutate({ userId });
    },
    [restoreMutation],
  );

  const handleSessionRecordingChange = useCallback(
    (userId: string, enabled: boolean) => {
      sessionRecordingMutation.mutate({ userId, data: { enabled } });
    },
    [sessionRecordingMutation],
  );

  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  }, []);

  const handleRecordStatusChange = useCallback(
    async (value: SuperadminUsersRecordStatusFilter) => {
      await navigate({
        to: '/superadmin/users',
        search: (prev) => ({
          ...prev,
          recordStatus: value === 'active' ? undefined : value,
        }),
        resetScroll: false,
      });
    },
    [navigate],
  );

  const handleSort = useCallback(
    (columnKey: string, direction: 'asc' | 'desc') => {
      const sortByMap: Record<string, 'name' | 'created' | 'lastVisited'> = {
        name: 'name',
        createdAt: 'created',
        lastVisited: 'lastVisited',
      };
      const newSortBy = sortByMap[columnKey];
      if (!newSortBy) {
        return;
      }
      navigate({
        to: '/superadmin/users',
        search: (prev) => ({
          ...prev,
          sortBy: newSortBy,
          sortDirection: direction,
        }),
        resetScroll: false,
      });
    },
    [navigate],
  );

  const items = useMemo(() => data?.pages.flatMap((p) => p.data.items) ?? [], [data]);

  const bulkArchiveTargets = useMemo(
    () =>
      items.filter((row) => selectedRowKeys.has(row.id) && !row.deletedAt && row.id !== account.id),
    [account.id, items, selectedRowKeys],
  );

  const bulkRestoreTargets = useMemo(
    () =>
      items.filter((row) => selectedRowKeys.has(row.id) && row.deletedAt && row.id !== account.id),
    [account.id, items, selectedRowKeys],
  );

  const handleBulkArchiveUsers = useCallback(() => {
    if (bulkArchiveTargets.length === 0) {
      return;
    }
    Modal.confirm({
      title: `Archive ${bulkArchiveTargets.length} user(s)?`,
      content:
        'The selected users will be archived and will not be able to sign in until restored.',
      okText: 'Archive',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await Promise.all(
            bulkArchiveTargets.map((row) => archiveMutation.mutateAsync({ userId: row.id })),
          );
          setSelectedRowKeys(new Set());
          message.success(`Archived ${bulkArchiveTargets.length} user(s)`);
        } catch {
          message.error('Failed to archive some users');
        }

        queryClient.invalidateQueries({ queryKey: ['superadmin', 'users'] });
      },
    });
  }, [archiveMutation, bulkArchiveTargets, queryClient]);

  const handleBulkRestoreUsers = useCallback(() => {
    if (bulkRestoreTargets.length === 0) {
      return;
    }
    Modal.confirm({
      title: `Restore ${bulkRestoreTargets.length} user(s)?`,
      content: 'The selected users will be able to sign in again.',
      okText: 'Restore',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await Promise.all(
            bulkRestoreTargets.map((row) => restoreMutation.mutateAsync({ userId: row.id })),
          );
          setSelectedRowKeys(new Set());
          message.success(`Restored ${bulkRestoreTargets.length} user(s)`);
        } catch {
          message.error('Failed to restore some users');
        }

        queryClient.invalidateQueries({ queryKey: ['superadmin', 'users'] });
      },
    });
  }, [bulkRestoreTargets, queryClient, restoreMutation]);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <>
      <Flex vertical style={{ height: `calc(100dvh - ${insets.top}px)`, minHeight: 0 }}>
        <Flex
          align="center"
          gap={16}
          wrap="wrap"
          style={{
            padding: isMobile ? '12px 12px' : '12px 24px',
            minHeight: 53,
            minWidth: 0,
            flexShrink: 0,
            backgroundColor: 'var(--ant-color-bg-layout)',
          }}
        >
          {isMobile ? (
            <Flex align="center" gap={8} style={{ width: '100%', minWidth: 0, flexShrink: 0 }}>
              <Input
                placeholder="Search users by name or email..."
                prefix={<Search size={14} />}
                value={searchInput}
                onChange={handleSearchChange}
                allowClear
                style={{ flex: 1, minWidth: 0 }}
              />
              <Button
                type="primary"
                size="small"
                icon={<Plus size={12} />}
                onClick={handleOpenCreateModal}
                title="Create SuperAdmin"
              />
            </Flex>
          ) : (
            <>
              <Input
                placeholder="Search users by name or email..."
                data-testid="superadmin-users-search"
                prefix={<Search size={14} />}
                value={searchInput}
                onChange={handleSearchChange}
                allowClear
                style={{
                  maxWidth: 400,
                  minWidth: 0,
                }}
              />
              <Flex align="center" gap={8} style={{ marginLeft: 'auto', flexShrink: 0 }}>
                <Button type="primary" icon={<Plus size={14} />} onClick={handleOpenCreateModal}>
                  Create SuperAdmin
                </Button>
              </Flex>
            </>
          )}
        </Flex>
        {selectedRowKeys.size > 0 ? (
          <Flex
            vertical={isMobile}
            align={isMobile ? 'stretch' : 'center'}
            gap={isMobile ? 8 : 12}
            wrap={isMobile ? false : 'wrap'}
            style={{
              padding: isMobile ? '8px 12px' : '8px 24px',
              flexShrink: 0,
              backgroundColor: 'var(--ant-color-fill-tertiary)',
              borderBottom: '1px solid var(--ant-color-border-secondary)',
            }}
          >
            <Typography.Text type="secondary">
              <span className="font-mono">{selectedRowKeys.size}</span> selected
            </Typography.Text>
            <Flex gap={8} style={{ width: isMobile ? '100%' : 'auto', minWidth: 0 }}>
              <Button
                size="small"
                danger
                disabled={bulkArchiveTargets.length === 0}
                onClick={handleBulkArchiveUsers}
                style={{ flex: isMobile ? 1 : undefined }}
              >
                Archive
              </Button>
              <Button
                size="small"
                disabled={bulkRestoreTargets.length === 0}
                onClick={handleBulkRestoreUsers}
                style={{ flex: isMobile ? 1 : undefined }}
              >
                Restore
              </Button>
            </Flex>
          </Flex>
        ) : null}
        <TableSurface className="table-surface--page-inset-top-nav table-surface--full-round">
          <SuperadminUsersTable
            dataSource={items}
            isPending={isPending}
            isError={isError}
            onSort={handleSort}
            onArchive={handleArchive}
            onRestore={handleRestore}
            onImpersonate={handleImpersonate}
            onSessionRecordingChange={handleSessionRecordingChange}
            onLoadMore={handleLoadMore}
            selectedRows={selectedRowKeys}
            onSelectedRowsChange={setSelectedRowKeys}
            statusFilterHeader={<UsersStatusFilterDropdown onChange={handleRecordStatusChange} />}
            hideRowActionsForUserId={account.id}
          />
        </TableSurface>
      </Flex>

      <Modal
        open={!!impersonateTarget}
        title={`Log in as ${impersonateTarget?.email ?? ''} (read-only)`}
        okText="Open session"
        onOk={() => handleImpersonateConfirm()}
        confirmLoading={impersonateMutation.isPending}
        onCancel={() => {
          setImpersonateTarget(null);
          impersonateForm.resetFields();
        }}
        destroyOnHidden
      >
        <Form form={impersonateForm} layout="vertical" validateTrigger="onSubmit">
          <Form.Item
            name="reason"
            label="Reason"
            rules={[{ required: true, message: 'Please enter a reason' }]}
          >
            <Input.TextArea
              placeholder="e.g. Investigating reported issue with company assignment"
              autoSize={{ minRows: 2, maxRows: 5 }}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={isCreateModalOpen}
        title="Create SuperAdmin"
        okText="Create"
        onOk={() => {
          void handleCreateSuperadmin();
        }}
        confirmLoading={createSuperadminMutation.isPending}
        onCancel={handleCloseCreateModal}
        destroyOnHidden
      >
        <Form form={createForm} layout="vertical" validateTrigger="onSubmit">
          <Form.Item
            name="firstName"
            label="First name"
            rules={[{ required: true, message: 'Please enter first name' }]}
          >
            <Input placeholder="Jane" autoComplete="given-name" />
          </Form.Item>
          <Form.Item
            name="lastName"
            label="Last name"
            rules={[{ required: true, message: 'Please enter last name' }]}
          >
            <Input placeholder="Doe" autoComplete="family-name" />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Enter a valid email' },
            ]}
          >
            <Input placeholder="jane@example.com" autoComplete="email" />
          </Form.Item>
          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: 'Please enter password' },
              { min: 8, message: 'At least 8 characters' },
            ]}
          >
            <Input.Password placeholder="••••••••" autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export const Route = createFileRoute('/_authenticated/superadmin/users')({
  component: UsersRoute,
  validateSearch: (params) => UsersSearchParamsSchema.parse(params),
});
